import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { modeloService } from '../../services/fashionService';
import { FashionModelo, MatrizLineaSeleccionada, MatrizModelo } from '../../types/fashion';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (lineas: MatrizLineaSeleccionada[]) => void;
  title?: string;
}

const MatrizSeleccionDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title = 'Agregar por matriz (talle × color)',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [modelos, setModelos] = useState<FashionModelo[]>([]);
  const [modelo, setModelo] = useState<FashionModelo | null>(null);
  const [matriz, setMatriz] = useState<MatrizModelo | null>(null);
  const [cantidades, setCantidades] = useState<Record<string, number>>({});
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [loadingMatriz, setLoadingMatriz] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoadingModelos(true);
        const res = await modeloService.getModelos({ pageNumber: 1, pageSize: 500 });
        setModelos(res.items.filter((m) => m.estado === 'A'));
      } catch {
        enqueueSnackbar('No se pudieron cargar modelos', { variant: 'error' });
      } finally {
        setLoadingModelos(false);
      }
    })();
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!open) {
      setModelo(null);
      setMatriz(null);
      setCantidades({});
    }
  }, [open]);

  const loadMatriz = async (m: FashionModelo | null) => {
    setModelo(m);
    setMatriz(null);
    setCantidades({});
    if (!m) return;
    try {
      setLoadingMatriz(true);
      const data = await modeloService.getMatriz(m.codigoModelo);
      setMatriz(data);
    } catch {
      enqueueSnackbar('No se pudo cargar la matriz del modelo', { variant: 'error' });
    } finally {
      setLoadingMatriz(false);
    }
  };

  const talles = useMemo(
    () => (matriz?.talles || []).slice().sort((a, b) => a.orden - b.orden),
    [matriz],
  );

  const colores = useMemo(() => {
    if (!matriz) return [];
    const codes = Array.from(new Set(matriz.celdas.filter((c) => c.existe).map((c) => c.codigoColor)));
    return codes.map((code) => ({
      code,
      name: matriz.colores.find((c) => c.codigoColor === code)?.nombreColor || code,
    }));
  }, [matriz]);

  const cellKey = (color: string, talle: string) => `${color}||${talle}`;

  const handleConfirm = () => {
    if (!matriz) return;
    const lineas: MatrizLineaSeleccionada[] = [];
    for (const celda of matriz.celdas) {
      if (!celda.existe || !celda.codigoArticulo) continue;
      const qty = cantidades[cellKey(celda.codigoColor, celda.codigoTalle)] || 0;
      if (qty <= 0) continue;
      lineas.push({
        codigoArticulo: celda.codigoArticulo,
        descripcion: `${matriz.descripcion} ${celda.nombreColor} ${celda.codigoTalle}`,
        cantidad: qty,
        precioUnitario: celda.precio ?? 0,
        costoUnitario: celda.costo ?? 0,
      });
    }
    if (lineas.length === 0) {
      enqueueSnackbar('Ingresá cantidades en al menos una celda', { variant: 'warning' });
      return;
    }
    onConfirm(lineas);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={modelos}
            loading={loadingModelos}
            getOptionLabel={(o) => `${o.codigoModelo} - ${o.descripcion}`}
            value={modelo}
            onChange={(_, v) => loadMatriz(v)}
            renderInput={(params) => <TextField {...params} label="Modelo" />}
          />
          {loadingMatriz && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
          {matriz && !loadingMatriz && (
            <>
              <Typography variant="body2" color="text.secondary">
                Ingresá cantidades por talle y color. Solo se agregan celdas con cantidad &gt; 0 y variante generada.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Color</TableCell>
                      {talles.map((t) => (
                        <TableCell key={t.codigoTalle} align="center">{t.codigoTalle}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {colores.map((color) => (
                      <TableRow key={color.code}>
                        <TableCell>{color.name}</TableCell>
                        {talles.map((t) => {
                          const celda = matriz.celdas.find(
                            (c) => c.codigoColor === color.code && c.codigoTalle === t.codigoTalle && c.existe,
                          );
                          const key = cellKey(color.code, t.codigoTalle);
                          return (
                            <TableCell key={t.codigoTalle} align="center" sx={{ minWidth: 80 }}>
                              {celda ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={cantidades[key] ?? ''}
                                  onChange={(e) => {
                                    const n = Number(e.target.value);
                                    setCantidades((prev) => ({
                                      ...prev,
                                      [key]: Number.isFinite(n) ? n : 0,
                                    }));
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                  helperText={celda.cantidad != null ? `Stock: ${celda.cantidad}` : undefined}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {colores.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={talles.length + 1} align="center">
                          Este modelo aún no tiene variantes generadas
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!matriz}>
          Agregar líneas
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatrizSeleccionDialog;
